import { useEffect, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { setEndUserDashboardStateResolved } from 'store/slices/authSlice';

import {
  canAccessEndUserFullFlows,
  canAccessEndUserTpaConnectionFlow,
  getDisabledEndUserSideMenuKeys,
} from 'services/endUser/endUserRouteAccess';
import { hydrateEndUserProfile } from 'services/endUser/hydrateEndUserProfile';
import { resolveEndUserAuth } from 'services/endUser/resolveEndUserAuth';

import type { RootState } from 'store/store';

/**
 * Shared end-user dashboard access flags from GET /eu/{euId}/state (Redux).
 * Ensures state is hydrated once per auth credentials for route guards and navigation.
 */
export function useEndUserDashboardAccess() {
  const dispatch = useDispatch();
  const euId = useSelector((state: RootState) => state.auth.euId);
  const idToken = useSelector((state: RootState) => state.userSession.idToken);
  const accountState = useSelector((state: RootState) => state.auth.endUserAccountState);
  const profileState = useSelector((state: RootState) => state.auth.endUserProfileState);
  const hasExpenses = useSelector((state: RootState) => state.auth.endUserHasExpenses);
  const isResolved = useSelector((state: RootState) => state.auth.endUserDashboardStateResolved);

  useEffect(() => {
    const auth = resolveEndUserAuth({ euId, idToken });
    if (!auth.euId || !auth.idToken) {
      return;
    }

    if (isResolved) {
      return;
    }

    void hydrateEndUserProfile(auth.euId, auth.idToken, dispatch).finally(() => {
      dispatch(setEndUserDashboardStateResolved(true));
    });
  }, [dispatch, euId, idToken, isResolved]);

  return useMemo(
    () => ({
      accountState,
      profileState,
      hasExpenses,
      isLoading: !isResolved,
      canAccessFullFlows: canAccessEndUserFullFlows(accountState, hasExpenses),
      canAccessTpaConnectionFlow: canAccessEndUserTpaConnectionFlow(accountState),
      disabledSideMenuKeys: getDisabledEndUserSideMenuKeys(accountState, hasExpenses),
    }),
    [accountState, profileState, hasExpenses, isResolved]
  );
}
